import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { CalendarPlus, Download } from 'lucide-react'
import type { ReactNode } from 'react'
import { t } from '../lib/i18n.js'

/**
 * "Add to calendar" target picker.
 *
 * Exists because a bare .ics download is a confusing dead end on mobile: the
 * file drops into the notification shade and the user — who just tapped *add
 * to calendar* — sees no calendar and assumes nothing happened. Offering the
 * destination up front makes the outcome predictable.
 *
 * Two options, because they cover the real split rather than every vendor:
 *  - GOOGLE: a URL to Google's own event screen. No download, and the user
 *    lands somewhere that obviously worked. The common Android path.
 *  - .ics FILE: Apple Calendar, Outlook, and everything else. On iOS this is
 *    the ONLY option — Safari has no add-event URL scheme — and it is handled
 *    well there: the file opens Calendar's import sheet directly.
 */

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Google Calendar's pre-filled event screen. */
  googleUrl: string
  /** Our own .ics endpoint. A real navigation, so the browser downloads it. */
  icsUrl: string
}

export function CalendarDialog({ open, onOpenChange, googleUrl, icsUrl }: Props) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="dialog-overlay" />
        <AlertDialog.Content className="dialog">
          <AlertDialog.Title className="dialog-title">
            {t('done.calendar')}
          </AlertDialog.Title>
          {/* Radix requires a description for the dialog to be announced
              properly; the options below are self-explanatory, so it is the
              question itself. */}
          <AlertDialog.Description className="faint">
            {t('cal.where')}
          </AlertDialog.Description>

          <div className="stack mt">
            <Option
              icon={<CalendarPlus size={20} aria-hidden />}
              label={t('cal.google')}
              hint={t('cal.googleHint')}
              href={googleUrl}
              external
              // Close behind the new tab, so coming back does not land on a
              // stale sheet.
              onSelect={() => onOpenChange(false)}
            />
            <Option
              icon={<Download size={20} aria-hidden />}
              label={t('cal.ics')}
              hint={t('cal.icsHint')}
              href={icsUrl}
              onSelect={() => onOpenChange(false)}
            />
          </div>

          <AlertDialog.Cancel asChild>
            <button className="btn btn-ghost mt-sm">{t('cal.cancel')}</button>
          </AlertDialog.Cancel>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

function Option({
  icon,
  label,
  hint,
  href,
  external,
  onSelect,
}: {
  icon: ReactNode
  label: string
  hint: string
  href: string
  external?: boolean
  onSelect: () => void
}) {
  return (
    <a
      className="cal-option"
      href={href}
      onClick={onSelect}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      <span className="cal-option-icon">{icon}</span>
      <span className="cal-option-text">
        <span className="cal-option-label">{label}</span>
        <span className="faint">{hint}</span>
      </span>
    </a>
  )
}
